import { Link, useLocation } from "react-router-dom";
import { Bell, Building2, CalendarCheck, ChartColumn, ClipboardList, LayoutGrid, MapPin, Package, Palette, Settings, Truck, Users, type LucideIcon } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { SidebarAccount } from "@/components/layout/SidebarAccount";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "@/components/ui/sidebar";
import { STAMMDATEN_SUB } from "@/features/onboarding/config";
import { TENANT_SUB_IDS, type StammdatenSubId } from "@/features/onboarding/progress";
import { useSession } from "@/lib/session/SessionContext";
import { usesMobileApp } from "@shared/roles.js";

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Zahl neben dem Punkt — nur gerendert, wenn sie größer als null ist. */
  badge?: number;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

/**
 * Die `icon`-Zeichenkette aus STAMMDATEN_SUB, endlich eingelöst: das Feld stand
 * seit dem Wizard in der Konfiguration, ohne dass es je jemand gezeichnet hätte.
 * Hier statt in config.ts, damit die gemeinsame Konfiguration keine
 * React-Abhängigkeit bekommt.
 */
const SUB_ICONS: Record<string, LucideIcon> = {
  "building-2": Building2,
  users: Users,
  truck: Truck,
  palette: Palette,
  "map-pin": MapPin,
  package: Package
};

function stammdatenGroup(): NavGroup {
  const ids = TENANT_SUB_IDS as readonly StammdatenSubId[];
  return {
    label: "Stammdaten",
    items: STAMMDATEN_SUB.filter((sub) => ids.includes(sub.id)).map((sub) => ({
      label: sub.title,
      to: `/app/stammdaten/${sub.id}`,
      icon: SUB_ICONS[sub.icon] ?? Package
    }))
  };
}

/**
 * Die linke Leiste — die Navigation der Büro-Ansichten. Sie trägt zwei Bäume,
 * je nachdem wer angemeldet ist: Bearbeiter sehen den Onboarding-Bereich,
 * Mandanten-Mitglieder ihren Betrieb. Beide sind hier und nicht in zwei Dateien,
 * weil sie sich Kopf, Fuß und Rahmen teilen und nur die Punkte unterscheiden.
 *
 * Die Kundensitzung kommt hier nie an: der Onboarding-Wizard bringt seinen
 * eigenen Rahmen mit (OnboardingLayout.tsx).
 */
export function AppSidebar() {
  const { session, notifications } = useSession();
  const { pathname } = useLocation();

  const istBearbeiter = session?.actorType === "admin";
  const rolle = session?.actorType === "member" ? session.role : null;
  const ungelesen = notifications.filter((notification) => !notification.readAt).length;

  const groups: NavGroup[] = istBearbeiter
    ? [
        {
          items: [
            { label: "Dashboard", to: "/admin", icon: LayoutGrid },
            { label: "Analyse", to: "/admin/analyse", icon: ChartColumn },
            // Die Glocke aus der alten Kopfzeile: als Menüpunkt trägt sie die
            // Anzahl statt nur einen roten Punkt.
            { label: "Mitteilungen", to: "/admin/notifications", icon: Bell, badge: ungelesen },
            { label: "Einstellungen", to: "/admin/settings", icon: Settings }
          ]
        }
      ]
    : [
        {
          label: "Betrieb",
          items: [...(rolle && usesMobileApp(rolle) ? [{ label: "Mein Tag", to: "/app/heute", icon: CalendarCheck }] : []), { label: "Dispoboard", to: "/app/dispo", icon: LayoutGrid }, { label: "Aufträge", to: "/app/auftraege", icon: ClipboardList }]
        },
        stammdatenGroup()
      ];

  const home = istBearbeiter ? "/admin" : "/app";

  // role explizit: `Sidebar` rendert ein div, und ein aria-label an einem div ohne Rolle benennt nichts.
  return (
    <Sidebar collapsible="icon" role="navigation" aria-label="Hauptnavigation">
      <SidebarHeader className="h-(--header-h) justify-center border-b border-sidebar-border px-4 group-data-[collapsible=icon]:px-2">
        <Link to={home} aria-label="Zur Startseite" className="flex items-center gap-2 overflow-hidden">
          {/*
            Eingeklappt ist die Leiste 48px breit — der Schriftzug passt dort
            nicht und stünde als „clev" abgeschnitten da. Dann die quadratische
            Marke, dieselbe wie im Browser-Tab (public/app-icon.svg).

            Das Umschalten sitzt am `span` und nicht am `Logo` selbst: die
            Komponente setzt `display: block` als Inline-Stil (Logo.tsx), und
            der schlägt jede Klasse aus dem Stylesheet.
          */}
          <span className="shrink-0 group-data-[collapsible=icon]:hidden">
            <Logo className="h-6" />
          </span>
          <img src="/app-icon.svg" alt="" className="hidden size-6 shrink-0 rounded-sm group-data-[collapsible=icon]:block" />
          {istBearbeiter ? <span className="truncate rounded-full bg-sidebar-accent px-2 py-[0.15rem] text-xs group-data-[collapsible=icon]:hidden">Onboarding</span> : null}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {groups.map((group, index) => (
          <SidebarGroup key={group.label ?? index}>
            {group.label ? <SidebarGroupLabel>{group.label}</SidebarGroupLabel> : null}
            <SidebarMenu>
              {group.items.map((item) => {
                const aktiv = pathname === item.to;
                return (
                  <SidebarMenuItem key={item.to}>
                    {/*
                      Die Zahl gehört in den Namen des Links, nicht nur ans
                      Abzeichen: `SidebarMenuBadge` rendert ein div neben dem
                      Knopf, ohne Rolle und eingeklappt ganz ausgeblendet — wer
                      vorlesen lässt, hörte sonst „9+" ohne Bezug oder gar
                      nichts. Der sichtbare Text steht vorn (WCAG „Label in
                      Name"), das Abzeichen selbst wird zur Dekoration.
                    */}
                    <SidebarMenuButton isActive={aktiv} tooltip={item.label} aria-label={item.badge ? `${item.label}, ${item.badge} ungelesen` : undefined} render={<Link to={item.to} aria-current={aktiv ? "page" : undefined} />}>
                      <item.icon aria-hidden="true" />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                    {item.badge ? <SidebarMenuBadge aria-hidden="true">{item.badge > 9 ? "9+" : item.badge}</SidebarMenuBadge> : null}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarAccount />
      </SidebarFooter>

      <SidebarRail aria-label="Navigation umschalten" title="Navigation umschalten" />
    </Sidebar>
  );
}
